if (!customElements.get('sown-pdp-gallery')) {
  customElements.define(
    'sown-pdp-gallery',
    class SownPdpGallery extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.abortController = new AbortController();
        this.signal = this.abortController.signal;
        this.track = this.querySelector('[data-gallery-track]');
        this.slides = Array.from(this.querySelectorAll('.sown-gallery__slide'));
        this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
        this.rail = this.querySelector('[data-gallery-thumbs]');
        this.dialog = this.querySelector('[data-gallery-dialog]');
        this.trackWidth = this.track?.clientWidth;
        this.current = 0;
        this.dialogIndex = 0;
        this.lastOpener = null;
        this.scrollFrame = 0;
        if (!this.track || !this.slides.length) return;

        this.classList.add('is-ready');
        this.bindEvents();
        if (typeof ResizeObserver === 'function') {
          this.resizeObserver = new ResizeObserver(() => this.realignCurrentSlide());
          this.resizeObserver.observe(this);
        }
        this.subscribeToVariantChanges();
        this.select(0);
        if (this.dataset.initialMediaId) this.setActiveMedia(this.dataset.initialMediaId);
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.resizeObserver?.disconnect();
        this.unsubscribeVariantChanges?.();
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
        this.pauseAllVideos();
        if (this.dialog?.open) this.dialog.close();
        this.classList.remove('is-ready');
        this.ready = false;
      }

      bindEvents() {
        const options = { signal: this.signal };
        let pointerStart = null;
        this.didDrag = false;
        this.track.addEventListener('scroll', () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = 0;
            if (this.track.clientWidth !== this.trackWidth) this.realignCurrentSlide();
            else this.updateSelected(this.closestSlide());
          });
        }, { ...options, passive: true });
        this.track.addEventListener('pointerdown', (event) => {
          pointerStart = { x: event.clientX, y: event.clientY };
          this.didDrag = false;
        }, { ...options, passive: true });
        this.track.addEventListener('pointermove', (event) => {
          if (pointerStart && Math.abs(event.clientX - pointerStart.x) > 8) this.didDrag = true;
        }, { ...options, passive: true });
        this.track.addEventListener('pointerup', () => { pointerStart = null; }, { ...options, passive: true });
        this.track.addEventListener('pointercancel', () => { pointerStart = null; }, { ...options, passive: true });
        this.track.addEventListener('keydown', (event) => {
          if (event.target.closest('input, textarea, select, video, [contenteditable="true"]')) return;
          if (event.key === 'ArrowLeft') { event.preventDefault(); this.select(this.current - 1, true); }
          if (event.key === 'ArrowRight') { event.preventDefault(); this.select(this.current + 1, true); }
        }, options);

        this.thumbs.forEach((thumb, index) => {
          thumb.addEventListener('click', (event) => {
            event.preventDefault();
            this.select(index, true);
          }, options);
        });
        this.querySelector('[data-gallery-previous]')?.addEventListener('click', () => this.select(this.current - 1, true), options);
        this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.select(this.current + 1, true), options);
        this.querySelector('[data-gallery-rail-previous]')?.addEventListener('click', () => this.moveRail(-1), options);
        this.querySelector('[data-gallery-rail-next]')?.addEventListener('click', () => this.moveRail(1), options);
        this.rail?.addEventListener('scroll', () => this.updateRailArrows(), { ...options, passive: true });

        this.querySelectorAll('[data-gallery-open]').forEach((opener) => {
          opener.addEventListener('click', (event) => {
            if (this.didDrag) {
              event.preventDefault();
              this.didDrag = false;
              return;
            }
            if (!this.dialog?.showModal) return;
            event.preventDefault();
            const index = this.slides.indexOf(opener.closest('.sown-gallery__slide'));
            if (index >= 0) this.openDialog(index, opener);
          }, options);
        });
        this.querySelector('[data-gallery-close]')?.addEventListener('click', () => this.dialog?.close(), options);
        this.querySelector('[data-gallery-dialog-previous]')?.addEventListener('click', () => this.showDialogMedia(this.dialogIndex - 1), options);
        this.querySelector('[data-gallery-dialog-next]')?.addEventListener('click', () => this.showDialogMedia(this.dialogIndex + 1), options);
        this.dialog?.addEventListener('close', () => {
          this.pauseDialogVideo();
          // Return focus without letting the browser scroll the track; WebKit otherwise runs a late
          // smooth scroll back to the dialog's slide that overrides a thumbnail tapped right after close.
          const opener = this.slides[this.current]?.querySelector('[data-gallery-open]') || this.lastOpener;
          opener?.focus({ preventScroll: true });
          this.scrollToSlide(this.current);
        }, options);
        this.dialog?.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        }, options);
        this.dialog?.addEventListener('keydown', (event) => {
          if (event.target.closest('input, textarea, select, video, [contenteditable="true"]')) return;
          if (event.key === 'ArrowLeft') { event.preventDefault(); this.showDialogMedia(this.dialogIndex - 1); }
          if (event.key === 'ArrowRight') { event.preventDefault(); this.showDialogMedia(this.dialogIndex + 1); }
        }, options);
      }

      subscribeToVariantChanges() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.unsubscribeVariantChanges = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const detail = event?.data;
          if (!detail || detail.sectionId !== this.dataset.sectionId) return;
          const formVariant = document.querySelector(`#product-form-${this.dataset.sectionId} .product-variant-id`);
          if (formVariant?.value && String(formVariant.value) !== String(detail.variant?.id)) return;
          this.setActiveMedia(detail.variant?.featured_media?.id);
        });
      }

      setActiveMedia(mediaId) {
        const index = this.slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
        if (index >= 0) this.select(index);
      }

      select(index, playVideo = false) {
        this.updateSelected(index);
        this.scrollToSlide(this.current);
        if (playVideo) this.playVideo(this.slides[this.current]?.querySelector('video'));
      }

      updateSelected(index) {
        this.current = Math.max(0, Math.min(Number(index) || 0, this.slides.length - 1));
        const slide = this.slides[this.current];
        this.dataset.activeMediaType = slide.dataset.mediaType;
        this.slides.forEach((item, itemIndex) => {
          item.classList.toggle('is-active', itemIndex === this.current);
          if (itemIndex !== this.current) item.querySelector('video')?.pause();
        });
        this.thumbs.forEach((thumb, thumbIndex) => {
          const active = thumbIndex === this.current;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-current', String(active));
        });
        const current = this.querySelector('[data-gallery-current]');
        if (current) current.textContent = this.current + 1;
        this.querySelector('[data-gallery-previous]')?.toggleAttribute('disabled', this.current === 0);
        this.querySelector('[data-gallery-next]')?.toggleAttribute('disabled', this.current === this.slides.length - 1);
        this.updateRailArrows();
        this.revealThumb(this.current);
      }

      closestSlide() {
        const trackBox = this.track.getBoundingClientRect();
        const center = trackBox.left + trackBox.width / 2;
        return this.slides.reduce((best, slide, index) => {
          const box = slide.getBoundingClientRect();
          const distance = Math.abs(box.left + box.width / 2 - center);
          return distance < best.distance ? { index, distance } : best;
        }, { index: 0, distance: Infinity }).index;
      }

      realignCurrentSlide() {
        this.trackWidth = this.track.clientWidth;
        this.scrollToSlide(this.current);
        this.updateSelected(this.current);
      }

      scrollToSlide(index) {
        const target = this.slides[index];
        if (!target) return;
        // Direct selections jump to their target; touch swipes retain native scroll snapping.
        this.track.scrollTo({ left: target.offsetLeft, behavior: 'instant' });
      }

      revealThumb(index) {
        const thumb = this.thumbs[index];
        if (!thumb || !this.rail) return;
        const rail = this.rail.getBoundingClientRect();
        const item = thumb.getBoundingClientRect();
        const vertical = this.isVerticalRail();
        let visibleStart = vertical ? rail.top : rail.left;
        let visibleEnd = vertical ? rail.bottom : rail.right;
        const previous = this.querySelector('[data-gallery-rail-previous]');
        const next = this.querySelector('[data-gallery-rail-next]');
        [previous, next].forEach((arrow) => {
          if (!arrow || arrow.disabled) return;
          const box = arrow.getBoundingClientRect();
          if (vertical && box.bottom > visibleStart && box.top < visibleEnd) {
            if (box.top <= visibleStart + (visibleEnd - visibleStart) / 2) visibleStart = Math.max(visibleStart, box.bottom);
            else visibleEnd = Math.min(visibleEnd, box.top);
          }
          if (!vertical && box.right > visibleStart && box.left < visibleEnd) {
            if (box.left <= visibleStart + (visibleEnd - visibleStart) / 2) visibleStart = Math.max(visibleStart, box.right);
            else visibleEnd = Math.min(visibleEnd, box.left);
          }
        });
        const itemStart = vertical ? item.top : item.left;
        const itemEnd = vertical ? item.bottom : item.right;
        const delta = itemStart < visibleStart ? itemStart - visibleStart : itemEnd > visibleEnd ? itemEnd - visibleEnd : 0;
        if (delta) this.rail.scrollBy(vertical ? { top: delta, behavior: this.scrollBehavior() } : { left: delta, behavior: this.scrollBehavior() });
      }

      isVerticalRail() { return window.matchMedia('(min-width: 1024px)').matches; }

      moveRail(direction) {
        if (!this.rail) return;
        this.rail.scrollBy(this.isVerticalRail()
          ? { top: direction * this.rail.clientHeight, behavior: this.scrollBehavior() }
          : { left: direction * this.rail.clientWidth, behavior: this.scrollBehavior() });
      }

      updateRailArrows() {
        if (!this.rail) return;
        const vertical = this.isVerticalRail();
        const position = vertical ? this.rail.scrollTop : this.rail.scrollLeft;
        const maximum = vertical ? this.rail.scrollHeight - this.rail.clientHeight : this.rail.scrollWidth - this.rail.clientWidth;
        this.querySelector('[data-gallery-rail-previous]')?.toggleAttribute('disabled', position <= 1 || maximum <= 1);
        this.querySelector('[data-gallery-rail-next]')?.toggleAttribute('disabled', position >= maximum - 1 || maximum <= 1);
      }

      openDialog(index, opener) {
        this.pauseAllVideos();
        this.lastOpener = opener;
        this.showDialogMedia(index);
        this.dialog.showModal();
        this.playVideo(this.dialog.querySelector('video'));
        this.querySelector('[data-gallery-close]')?.focus();
      }

      showDialogMedia(index) {
        this.dialogIndex = Math.max(0, Math.min(index, this.slides.length - 1));
        const content = this.querySelector('[data-gallery-dialog-content]');
        const template = this.slides[this.dialogIndex]?.querySelector('[data-gallery-dialog-template]');
        if (!content || !template) return;
        this.pauseDialogVideo();
        content.replaceChildren(template.content.cloneNode(true));
        const count = this.querySelector('[data-gallery-dialog-count]');
        if (count) count.textContent = `${this.dialogIndex + 1} / ${this.slides.length}`;
        this.querySelector('[data-gallery-dialog-previous]')?.toggleAttribute('disabled', this.dialogIndex === 0);
        this.querySelector('[data-gallery-dialog-next]')?.toggleAttribute('disabled', this.dialogIndex === this.slides.length - 1);
        this.select(this.dialogIndex);
        if (this.dialog.open) this.playVideo(content.querySelector('video'));
      }

      playVideo(video) {
        if (!video) return;
        video.playsInline = true;
        // Keep native controls available if the browser declines playback.
        video.play()?.catch(() => {});
      }

      pauseDialogVideo() { this.dialog?.querySelector('video')?.pause(); }
      pauseAllVideos() { this.querySelectorAll('video').forEach((video) => video.pause()); }
      scrollBehavior() { return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }
    }
  );
}
